import React, { useState } from "react";
import { Col, Form, message, Row, Flex, Tabs } from "antd";
import { useSelector, useDispatch } from "react-redux";
import {
  getSingleDimensionAction,
  getClientFormBuilderAction,
  updateClientFormBuilderAction,
  uploadDocumentCustomerDynamicFormAction,
} from "../../../../../../../../store/action/admin/dimensionsEntityActions";

import { buildStaticPayload, buildDynamicFormPayload } from "./formBuilders";
import { uploadFilesIfNeeded } from "./fileUpload";
import { useFormEffects } from "./useFormEffects";
import { GeneralDetailsCard } from "./GeneralDetailsCard";
import { AddressCard } from "./AddressCard";
import { PointOfContactCard } from "./PointOfContactCard";
import { DynamicFormCard } from "./DynamicFormCard";
import CustomLoaderIcon from "../../../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";

const CreateCustomer = React.forwardRef(
  ({ onClose, open, isCreateMode, onTabChange }, ref) => {
    const dispatch = useDispatch();
    const themeDefault = useSelector((state) => state.themeDefault);
    const singleEntityState = useSelector((state) => state.getSingleEntityList);
    const getSingleDimensionList = useSelector(
      (state) => state.getSingleDimensionList
    );
    const allTradeLicenseList = useSelector(
      (state) => state.allTradeLicenseList
    );
    const tradeLicenseList = allTradeLicenseList?.getAllTradeLicenseInfo || [];
    const getFormBuilderList = useSelector((state) => state.getFormBuilderList);
    const dynamicFormTemplate = getFormBuilderList?.info || null;
    const getClientFormBuilderList = useSelector(
      (state) => state.getClientFormBuilderList
    );
    const clientFormBuilderList = getClientFormBuilderList?.info || null;
    // Loading states
    const isSingleDimensionLoading = getSingleDimensionList?.loading || false;
    const isFormBuilderLoading = getFormBuilderList?.loading || false;
    const isClientFormBuilderLoading =
      getClientFormBuilderList?.loading || false;
    const isTradeLicenseLoading = allTradeLicenseList?.Loading || false;

    // Check if any data is loading
    const isLoading =
      isSingleDimensionLoading ||
      isFormBuilderLoading ||
      isClientFormBuilderLoading ||
      isTradeLicenseLoading;

    const entityId = singleEntityState?.info?._id || null;
    const dimensionId = getSingleDimensionList?.info?.dimension?._id || null;
    const parentDimensionId =
      getSingleDimensionList?.info?.dimension?.parent?._id ||
      getSingleDimensionList?.info?.parentDimensionId ||
      null;

    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState("overview");
    const [createdDimensionId, setCreatedDimensionId] = useState(null);

    // Use createdDimensionId if available (post-creation), otherwise use dimensionId
    const effectiveDimensionId = createdDimensionId || dimensionId;
    const effectiveIsCreateMode = isCreateMode && !createdDimensionId;

    // Initialization logic is handled in useFormEffects to avoid duplication
    useFormEffects({
      open,
      isCreateMode: effectiveIsCreateMode,
      form,
      entityId,
      dimensionId: effectiveDimensionId,
      dispatch,
      dynamicFormTemplate,
      clientFormBuilderList,
    });

    const handleSubmit = async () => {
      try {
        if (!entityId) {
          message.error("Entity ID is required. Please refresh and try again.");
          throw new Error("Entity ID is required");
        }

        let values;
        if (isCreateMode && !createdDimensionId) {
          const allFormValues = form.getFieldsValue(true);
          try {
            await form.validateFields();
          } catch (validationError) {
            throw validationError;
          }
          values = allFormValues;
        } else {
          values = await form.validateFields();
        }

        const updatedFormValues = await uploadFilesIfNeeded(
          values,
          dynamicFormTemplate,
          dispatch,
          form,
          uploadDocumentCustomerDynamicFormAction
        );

        values = updatedFormValues;

        const freshFormValues = form.getFieldsValue();

        if (
          freshFormValues.formQuestions &&
          Array.isArray(freshFormValues.formQuestions)
        ) {
          freshFormValues.formQuestions.forEach((q, idx) => {
            if (q?.answer && Array.isArray(q.answer) && q.answer.length > 0) {
              const fileItem = q.answer[0];
              if (fileItem?.url || fileItem?.publicId) {
                if (!values.formQuestions) {
                  values.formQuestions = [];
                }
                if (!values.formQuestions[idx]) {
                  values.formQuestions[idx] = { answer: [] };
                }
                values.formQuestions[idx].answer = q.answer;
                values[`formQuestions[${idx}].answer`] = q.answer;
              }
            }
          });
        }

        const staticPayload = buildStaticPayload(
          values,
          entityId,
          parentDimensionId
        );
        const dynamicPart = buildDynamicFormPayload(
          values,
          dynamicFormTemplate
        );

        const finalPayload = {
          ...staticPayload,
          ...dynamicPart,
          entityId: String(entityId),
        };

        const tradeLicenseIdValue =
          values.tradeLicenseId ||
          staticPayload.tradeLicenseId ||
          finalPayload.tradeLicenseId;

        if (tradeLicenseIdValue) {
          finalPayload.tradeLicenseId = String(tradeLicenseIdValue);
        } else {
          finalPayload.tradeLicenseId = null;
        }

        // Use createdDimensionId if available (post-creation update), otherwise use dimensionId
        const effectiveDimensionId = createdDimensionId || dimensionId;

        if (isCreateMode && !createdDimensionId) {
        } else if (effectiveDimensionId) {
          const payloadToSend = {
            ...finalPayload,
            entityId: String(entityId),
          };

          if (finalPayload.tradeLicenseId) {
            payloadToSend.tradeLicenseId = String(finalPayload.tradeLicenseId);
          }

          await dispatch(
            updateClientFormBuilderAction(effectiveDimensionId, payloadToSend)
          );
          message.success("Customer dimension updated successfully");
          await dispatch(getSingleDimensionAction(effectiveDimensionId));
          await dispatch(getClientFormBuilderAction(effectiveDimensionId));
          return null;
        }

        if (!isCreateMode) {
          message.error("Dimension ID is required for update");
          throw new Error("Dimension ID is required for update");
        }

        try {
          if (!finalPayload.clientId) {
            message.error("Customer ID is required");
            throw new Error("Customer ID is required");
          }

          const payloadToSend = {
            ...finalPayload,
            entityId: String(entityId),
          };

          const tradeLicenseIdValue =
            values.tradeLicenseId ||
            finalPayload.tradeLicenseId ||
            staticPayload.tradeLicenseId;

          if (tradeLicenseIdValue) {
            payloadToSend.tradeLicenseId = String(tradeLicenseIdValue);
          } else {
            payloadToSend.tradeLicenseId = null;
          }
          return payloadToSend;
        } catch (createErr) {
          throw createErr;
        }
      } catch (error) {
        if (!isCreateMode) {
          const errMsg =
            error?.message || error || "Failed to update customer dimension";
          message.error(errMsg);
        } else {
          message.error(error?.message || "Failed to create customer");
        }
        throw error;
      }
    };

    const handleCancel = () => {
      form.resetFields();
      onClose?.();
    };

    const handleNext = async () => {
      if (isCreateMode && !createdDimensionId) {
        try {
          await form.validateFields([
            "customerId",
            "customerName",
            "customerEmail",
          ]);
          setActiveTab("custom");
          onTabChange?.("custom");
        } catch (validationError) {
          throw validationError;
        }
      }
    };

    React.useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      cancel: handleCancel,
      next: handleNext,
      getActiveTab: () => activeTab,
      switchToCustomTab: async (dimensionIdFromCreate) => {
        if (dimensionIdFromCreate) {
          setCreatedDimensionId(dimensionIdFromCreate);
          // Fetch the created customer data to populate the form
          try {
            await dispatch(getClientFormBuilderAction(dimensionIdFromCreate));
          } catch (error) {
            console.error("Failed to fetch customer form builder:", error);
          }
        }
        setActiveTab("custom");
      },
    }));

    if (isLoading) {
      return (
        <Flex
          justify="center"
          align="center"
          style={{ width: "100%", minHeight: "400px" }}
        >
          <CustomLoaderIcon size="large" />
        </Flex>
      );
    }

    return (
      <Form form={form} layout="vertical" style={{ width: "100%" }}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            onTabChange?.(key);
          }}
          items={[
            {
              key: "overview",
              label: "Overview",
              children: (
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <GeneralDetailsCard
                      themeDefault={themeDefault}
                      tradeLicenseList={tradeLicenseList}
                      form={form}
                      isCreateMode={isCreateMode}
                    />
                  </Col>

                  <Col span={12}>
                    <AddressCard themeDefault={themeDefault} />
                  </Col>

                  <Col span={24}>
                    <PointOfContactCard form={form} />
                  </Col>
                </Row>
              ),
            },
            {
              key: "custom",
              label: "Custom",
              children: (
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <DynamicFormCard
                      themeDefault={themeDefault}
                      dynamicFormTemplate={dynamicFormTemplate}
                      clientFormBuilderList={
                        isCreateMode && !createdDimensionId
                          ? null
                          : clientFormBuilderList
                      }
                      dispatch={dispatch}
                      uploadDocumentCustomerDynamicFormAction={
                        uploadDocumentCustomerDynamicFormAction
                      }
                      form={form}
                      isCreateMode={isCreateMode && !createdDimensionId}
                    />
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </Form>
    );
  }
);

export default CreateCustomer;
